import { useCallback, type Dispatch, type SetStateAction } from 'react'
import {
  SIGNUP_STEP_FIELDS,
  TOTAL_SIGNUP_STEPS,
  hasFieldErrors,
  resolveSignupStepErrors,
} from './validation'
import type {
  CustomerAuthField,
  CustomerAuthFieldErrorMap,
  CustomerAuthValues,
} from './validation'

type Params = {
  isSignup: boolean
  signupStep: number
  values: CustomerAuthValues
  clearFieldErrors: (fields?: CustomerAuthField[]) => void
  markFieldsTouched: (fields: CustomerAuthField[]) => void
  onClearError: () => void
  setFieldErrorKeys: Dispatch<SetStateAction<CustomerAuthFieldErrorMap>>
  setFormSubmitted: (value: boolean) => void
  setSignupStep: Dispatch<SetStateAction<number>>
  setSignupStepAttempted: (value: number | null) => void
}

export const useSignupStepNavigation = ({
  isSignup,
  signupStep,
  values,
  clearFieldErrors,
  markFieldsTouched,
  onClearError,
  setFieldErrorKeys,
  setFormSubmitted,
  setSignupStep,
  setSignupStepAttempted,
}: Params) => {
  const goToNextSignupStep = useCallback(() => {
    if (!isSignup || signupStep >= TOTAL_SIGNUP_STEPS) return

    onClearError()
    setSignupStepAttempted(signupStep)

    const stepErrors = resolveSignupStepErrors(signupStep, values)
    if (hasFieldErrors(stepErrors)) {
      markFieldsTouched(SIGNUP_STEP_FIELDS[signupStep] ?? [])
      setFieldErrorKeys(stepErrors)
      return
    }

    setFormSubmitted(false)
    setSignupStepAttempted(null)
    clearFieldErrors()
    setSignupStep((step) => Math.min(TOTAL_SIGNUP_STEPS, step + 1))
  }, [
    clearFieldErrors,
    isSignup,
    markFieldsTouched,
    onClearError,
    setFieldErrorKeys,
    setFormSubmitted,
    setSignupStep,
    setSignupStepAttempted,
    signupStep,
    values,
  ])

  const goToPreviousSignupStep = useCallback(() => {
    if (!isSignup || signupStep <= 1) return

    setFormSubmitted(false)
    setSignupStepAttempted(null)
    clearFieldErrors()
    onClearError()
    setSignupStep((step) => Math.max(1, step - 1))
  }, [clearFieldErrors, isSignup, onClearError, setFormSubmitted, setSignupStep, setSignupStepAttempted, signupStep])

  const goToSignupStep = useCallback((targetStep: number) => {
    if (!isSignup) return
    const normalizedTarget = Math.max(1, Math.min(TOTAL_SIGNUP_STEPS, Math.trunc(targetStep)))
    setFormSubmitted(false)
    setSignupStepAttempted(null)
    clearFieldErrors()
    onClearError()
    setSignupStep(normalizedTarget)
  }, [clearFieldErrors, isSignup, onClearError, setFormSubmitted, setSignupStep, setSignupStepAttempted])

  return {
    goToNextSignupStep,
    goToPreviousSignupStep,
    goToSignupStep,
  }
}
